import { useEffect, useMemo, useRef, useState } from "react";
import {
  Upload, Loader2, Trash2, Edit2, ExternalLink, Search, FileText, Image as ImageIcon, Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";

import { supabase } from "@/integrations/supabase/client";
import { fetchCurrentCompanyId } from "@/hooks/useCurrentCompany";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useAllExplodedViews, getExplodedViewSignedUrl, type ExplodedView } from "@/hooks/useExplodedViews";
import { useCompanyApplianceCategories } from "@/hooks/useCompanyApplianceCategories";

const ACCEPT = "image/png,image/jpeg,image/webp,application/pdf";

const fileTypeOf = (mime: string): "image" | "pdf" =>
  mime === "application/pdf" ? "pdf" : "image";

interface WorkflowItem {
  id: string;
  title: string;
  brand: string | null;
  appliance_type: string;
  category: string;
}

interface BrandRow { id: string; name: string }

export default function ExplodedViewsTab() {
  const { user } = useAuth();
  const { data: views, isLoading, refetch } = useAllExplodedViews();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploading, setUploading] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [filterBrand, setFilterBrand] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterLinked, setFilterLinked] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const { names: companyCategoryNames } = useCompanyApplianceCategories();

  const [editing, setEditing] = useState<ExplodedView | null>(null);
  const [deleting, setDeleting] = useState<ExplodedView | null>(null);
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [workflowItems, setWorkflowItems] = useState<WorkflowItem[]>([]);

  useEffect(() => {
    supabase.from("brands").select("id, name").order("name").then(({ data }) => {
      setBrands((data ?? []) as BrandRow[]);
    });
    supabase.from("workflow_items").select("id, title, brand, appliance_type, category").order("title")
      .then(({ data }) => setWorkflowItems((data ?? []) as WorkflowItem[]));
  }, []);

  const applianceTypes = useMemo(
    () => Array.from(new Set(workflowItems.map((w) => w.appliance_type))).sort(),
    [workflowItems],
  );

  const handleFiles = async (files: FileList | null) => {
    if (!files || !user) return;
    const arr = Array.from(files).filter((f) => ACCEPT.includes(f.type));
    if (arr.length === 0) {
      toast.error("Only PNG, JPG, WEBP or PDF files are supported");
      return;
    }
    for (const file of arr) {
      setUploading((p) => [...p, file.name]);
      try {
        const safeName = file.name.replace(/[^\w.\-]/g, "_");
        const path = `exploded-views/${user.id}/${Date.now()}-${safeName}`;
        const { error: upErr } = await supabase.storage
          .from("repair-guides")
          .upload(path, file, { contentType: file.type });
        if (upErr) throw upErr;

        const companyId = await fetchCurrentCompanyId(user.id);
        if (!companyId) throw new Error("You're not assigned to a company yet — contact your admin.");
        const { error: insErr } = await supabase.from("exploded_views").insert({
          user_id: user.id,
          company_id: companyId,
          title: file.name.replace(/\.[^.]+$/, ""),
          file_path: path,
          file_type: fileTypeOf(file.type),
          mime_type: file.type,
          file_size: file.size,
          original_filename: file.name,
        });
        if (insErr) throw insErr;
        toast.success(`Uploaded ${file.name}`);
      } catch (e) {
        console.error(e);
        toast.error(`Failed to upload ${file.name}`);
      } finally {
        setUploading((p) => p.filter((n) => n !== file.name));
        refetch();
      }
    }
  };

  const openView = async (v: ExplodedView) => {
    const url = await getExplodedViewSignedUrl(v.file_path);
    if (!url) return toast.error("Could not open file");
    const a = document.createElement("a");
    a.href = url; a.target = "_blank"; a.rel = "noopener noreferrer";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const performDelete = async (v: ExplodedView) => {
    await supabase.storage.from("repair-guides").remove([v.file_path]);
    const { error } = await supabase.from("exploded_views").delete().eq("id", v.id);
    if (error) toast.error("Failed to delete");
    else { toast.success("Exploded view deleted"); refetch(); }
    setDeleting(null);
  };

  const saveEdit = async () => {
    if (!editing) return;
    const { error } = await supabase.from("exploded_views").update({
      title: editing.title,
      description: editing.description,
      brand: editing.brand,
      appliance_type: editing.appliance_type,
      models: editing.models,
      workflow_item_id: editing.workflow_item_id,
    }).eq("id", editing.id);
    if (error) toast.error("Save failed");
    else { toast.success("Saved"); setEditing(null); refetch(); }
  };

  const allBrands = useMemo(
    () => Array.from(new Set([...(views ?? []).map((v) => v.brand).filter(Boolean), ...brands.map((b) => b.name)])) as string[],
    [views, brands],
  );

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return (views ?? []).filter((v) => {
      if (filterBrand !== "all" && v.brand !== filterBrand) return false;
      if (filterType !== "all" && v.file_type !== filterType) return false;
      if (filterLinked === "linked" && !v.workflow_item_id) return false;
      if (filterLinked === "unlinked" && v.workflow_item_id) return false;
      if (filterCategory !== "all" && v.appliance_type !== filterCategory) return false;
      if (!s) return true;
      return (
        v.title?.toLowerCase().includes(s) ||
        v.original_filename?.toLowerCase().includes(s) ||
        v.brand?.toLowerCase().includes(s) ||
        v.models?.some((m) => m.toLowerCase().includes(s))
      );
    });
  }, [views, search, filterBrand, filterType, filterLinked, filterCategory]);

  // Workflow items filtered for the editing dialog (by chosen brand/type when set)
  const editWorkflowOptions = useMemo(() => {
    if (!editing) return workflowItems;
    return workflowItems.filter((w) => {
      if (editing.brand && w.brand && w.brand.toLowerCase() !== editing.brand.toLowerCase()) return false;
      if (editing.appliance_type && w.appliance_type !== editing.appliance_type) return false;
      return true;
    });
  }, [editing, workflowItems]);

  return (
    <div>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Exploded Views</h2>
          <p className="text-sm text-muted-foreground">
            Upload technical exploded-view diagrams (image or PDF). Tag by brand, appliance type, models and an optional workflow step.
          </p>
        </div>
        <Button onClick={() => fileInputRef.current?.click()}>
          <Upload className="w-4 h-4 mr-2" /> Upload images / PDFs
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
        />
      </div>

      <div className="flex flex-wrap gap-3 items-center mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, model, filename..." className="pl-9" />
        </div>
        <SearchableSelect
          className="w-[180px]"
          value={filterBrand}
          onChange={(v) => setFilterBrand(v ?? "all")}
          placeholder="Brand"
          searchPlaceholder="Search brands…"
          emptyMessage="No brands match"
          items={[
            { value: "all", label: "All brands" },
            ...allBrands.map((b) => ({ value: b, label: b })),
          ]}
        />

        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="File type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All files</SelectItem>
            <SelectItem value="image">Images</SelectItem>
            <SelectItem value="pdf">PDFs</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterLinked} onValueChange={setFilterLinked}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Linked" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="linked">Linked to step</SelectItem>
            <SelectItem value="unlinked">Unlinked</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Appliance category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {companyCategoryNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {uploading.length > 0 && (
        <div className="mb-3 space-y-2">
          {uploading.map((n) => (
            <div key={n} className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-3 py-2 rounded-md">
              <Loader2 className="w-4 h-4 animate-spin" /> Uploading {n}...
            </div>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No exploded views yet. Upload an image or PDF to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((v) => {
            const linkedItem = workflowItems.find((w) => w.id === v.workflow_item_id);
            return (
              <div key={v.id} className="bg-card border border-border rounded-xl p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  {v.file_type === "pdf" ? <FileText className="w-5 h-5 text-primary" /> : <ImageIcon className="w-5 h-5 text-primary" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground truncate">{v.title || v.original_filename || "Untitled"}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap text-xs">
                    {v.brand && <Badge>{v.brand}</Badge>}
                    {v.appliance_type && <Badge variant="secondary">{v.appliance_type}</Badge>}
                    <Badge variant="outline" className="uppercase">{v.file_type}</Badge>
                    {v.models?.slice(0, 5).map((m) => <Badge key={m} variant="outline">{m}</Badge>)}
                    {v.models && v.models.length > 5 && <Badge variant="outline">+{v.models.length - 5}</Badge>}
                    {linkedItem && <Badge variant="outline">↳ {linkedItem.title}</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openView(v)} title="Open">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ ...v, models: v.models ?? [] })} title="Edit">
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeleting(v)} title="Delete">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit exploded view</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input value={editing.title ?? ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  rows={2}
                  value={editing.description ?? ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  placeholder="Optional notes about what this diagram shows."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Brand</Label>
                  <Select
                    value={editing.brand ?? "__none"}
                    onValueChange={(v) => setEditing({ ...editing, brand: v === "__none" ? null : v, workflow_item_id: null })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select brand" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">No brand</SelectItem>
                      {brands.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Appliance type</Label>
                  <Select
                    value={editing.appliance_type ?? "__none"}
                    onValueChange={(v) => setEditing({ ...editing, appliance_type: v === "__none" ? null : v, workflow_item_id: null })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Any type</SelectItem>
                      {applianceTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Models (comma-separated)</Label>
                <Input
                  value={editing.models?.join(", ") ?? ""}
                  onChange={(e) => setEditing({
                    ...editing,
                    models: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                  })}
                />
                <p className="text-xs text-muted-foreground mt-1">Used to surface this view in matching workflows.</p>
              </div>
              <div>
                <Label>Linked workflow step (optional)</Label>
                <Select
                  value={editing.workflow_item_id ?? "__none"}
                  onValueChange={(v) => setEditing({ ...editing, workflow_item_id: v === "__none" ? null : v })}
                >
                  <SelectTrigger><SelectValue placeholder="No linked step" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">No linked step</SelectItem>
                    {editWorkflowOptions.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.title}{w.brand ? ` · ${w.brand}` : ""} · {w.appliance_type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  When set, this view is highlighted on the linked step inside repair / refurbishment workflows.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this exploded view?</AlertDialogTitle>
            <AlertDialogDescription>
              The image / PDF and its tags will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleting && performDelete(deleting)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
